import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { managementReviewApi } from '../services/api';
import type { ManagementReviewStatus, ManagementReviewSummary } from '../services/api';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { toneFromMap, type Tone } from '../constants/tone';

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUSES: ManagementReviewStatus[] = ['draft', 'submitted', 'approved'];

const STATUS_TONE: Record<string, Tone> = { approved: 'success', submitted: 'warning' };
const statusTone = (status: string): Tone => toneFromMap(STATUS_TONE, status, 'neutral');

const fmtDate = (d?: string) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getDefaultStartDate = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().split('T')[0];
};

// ── Page ──────────────────────────────────────────────────────────────────────

export function ManagementReviewList() {
  const navigate     = useNavigate();
  const queryClient  = useQueryClient();
  const { success: toastSuccess, error: toastError } = useToast();

  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState({ title: '', period: '', meetingDate: '', reviewStartDate: getDefaultStartDate() });
  const [formError, setFormError] = useState('');

  const [search, setSearch]             = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ['management-reviews'],
    queryFn:  () => managementReviewApi.list(),
  });

  const searched = useMemo(() => {
    const q = search.toLowerCase();
    return reviews.filter((r) =>
      (!q || [r.title, r.period].some((v) => v?.toLowerCase().includes(q)))
      && (!filterStatus || r.status === filterStatus),
    );
  }, [reviews, search, filterStatus]);

  const { sorted: filtered, field: sortField, dir: sortDir, toggle: toggleSort } = useSortableData<ManagementReviewSummary, keyof ManagementReviewSummary>(
    searched,
    (r, f) => String(r[f] ?? ''),
    'meetingDate',
    'desc',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(filtered, 25);

  const createMutation = useMutation({
    mutationFn: managementReviewApi.create,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['management-reviews'] });
      setShowModal(false);
      setForm({ title: '', period: '', meetingDate: '', reviewStartDate: getDefaultStartDate() });
      toastSuccess('Management review created');
      navigate(`/management-reviews/${created.reviewId}`);
    },
    onError: (err: Error) => {
      toastError(err.message || 'Failed to create review');
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!form.title.trim()) return setFormError('Title is required');
    if (!form.period.trim()) return setFormError('Period is required');
    createMutation.mutate(form);
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <PageHeader
        icon="ri-clipboard-line"
        title="Management Reviews"
        subtitle="ISO 27001 Clause 9.3 — formal review of the ISMS by top management"
        actions={
          <button className="np-btn np-btn-primary" onClick={() => setShowModal(true)}>
            <i className="ri-add-line me-1"></i> New Review
          </button>
        }
      />

      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="np-card">
          <div className="np-card-body text-center py-5">
            <i className="ri-clipboard-line ri-48px mb-3 d-block" style={{ color: 'var(--bs-secondary-color)' }}></i>
            <h5 className="np-sub">No management reviews yet</h5>
            <p className="np-sub mb-3">
              Create your first ISO 27001 Clause 9.3 management review to get started.
            </p>
            <button className="np-btn np-btn-primary" onClick={() => setShowModal(true)}>
              <i className="ri-add-line me-1"></i> Create Review
            </button>
          </div>
        </div>
      ) : (
        <div className="np-card">
          {/* Filters toolbar */}
          <div className="np-card-body border-bottom">
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <div className="np-search" style={{ maxWidth: 280 }}>
                <i className="ri-search-line"></i>
                <input
                  type="text"
                  placeholder="Search title or period…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button className="np-icon-btn" onClick={() => setSearch('')}><i className="ri-close-line"></i></button>
                )}
              </div>
              <select
                className="np-select"
                style={{ maxWidth: 160 }}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
              {(search || filterStatus) && (
                <button
                  className="np-btn np-btn-ghost np-btn-sm"
                  onClick={() => { setSearch(''); setFilterStatus(''); }}
                >
                  <i className="ri-filter-off-line"></i>Clear
                </button>
              )}
              <span className="np-sub ms-auto">
                {filtered.length} result{filtered.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <NimbusTable
            thead={
              <tr>
                <SortableTh field="reviewId" active={sortField === 'reviewId'} dir={sortDir} onSort={toggleSort}>ID</SortableTh>
                <SortableTh field="title" active={sortField === 'title'} dir={sortDir} onSort={toggleSort}>Title</SortableTh>
                <SortableTh field="period" active={sortField === 'period'} dir={sortDir} onSort={toggleSort}>Period</SortableTh>
                <SortableTh field="meetingDate" active={sortField === 'meetingDate'} dir={sortDir} onSort={toggleSort}>Meeting Date</SortableTh>
                <SortableTh field="status" active={sortField === 'status'} dir={sortDir} onSort={toggleSort}>Status</SortableTh>
                <SortableTh field="createdAt" active={sortField === 'createdAt'} dir={sortDir} onSort={toggleSort}>Created</SortableTh>
                <SortableTh field="updatedAt" active={sortField === 'updatedAt'} dir={sortDir} onSort={toggleSort}>Last Updated</SortableTh>
              </tr>
            }
            isEmpty={pageItems.length === 0}
            emptyState={
              <div className="text-center py-5">
                <i className="ri-search-line ri-3x d-block mb-2 opacity-25"></i>
                <p className="np-sub mb-0">No results match your filters.</p>
              </div>
            }
          >
            {pageItems.map((r) => (
              <tr
                key={r.reviewId}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/management-reviews/${r.reviewId}`)}
              >
                <td>
                  <code className="text-nowrap">{r.reviewId}</code>
                </td>
                <td>
                  <Link to={`/management-reviews/${r.reviewId}`} className="np-row-title fw-semibold" onClick={(e) => e.stopPropagation()}>
                    {r.title}
                  </Link>
                </td>
                <td>{r.period || '—'}</td>
                <td className="np-tabular">{fmtDate(r.meetingDate)}</td>
                <td>
                  <span className={`np-chip np-tone-${statusTone(r.status)}`}>
                    {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                  </span>
                </td>
                <td className="np-sub np-tabular">{fmtDate(r.createdAt)}</td>
                <td className="np-sub np-tabular">{fmtDate(r.updatedAt)}</td>
              </tr>
            ))}
          </NimbusTable>
          {pageItems.length > 0 && (
            <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
          )}
        </div>
      )}

      {/* Create modal */}
      {showModal && (
        <NimbusModal size="lg" centered>
          <form onSubmit={handleCreate}>
            <NimbusModalContent
              title="New Management Review"
              subtitle="ISO 27001 §9.3 — Clause 9.3 review of the ISMS by top management"
              onClose={() => setShowModal(false)}
              footer={
                <>
                  <button type="button" className="np-btn np-btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="np-btn np-btn-primary" disabled={createMutation.isPending}>
                    {createMutation.isPending
                      ? <><span className="spinner-border spinner-border-sm me-1" />Creating...</>
                      : 'Create Review'}
                  </button>
                </>
              }
            >
              {formError && (
                <div className="np-banner np-tone-danger mb-4">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </div>
              )}
              <div className="np-field">
                <label>Title <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="np-input"
                  placeholder="e.g. Annual ISMS Management Review Q1 2026"
                  value={form.title}
                  autoFocus
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div className="row g-3 mb-3">
                <div className="col-md-6 np-field">
                  <label>Period <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="np-input"
                    placeholder="e.g. Q1 2026 or Jan–Mar 2026"
                    value={form.period}
                    onChange={(e) => setForm({ ...form, period: e.target.value })}
                  />
                </div>
                <div className="col-md-6 np-field">
                  <label>Meeting Date <span className="np-sub fw-normal">(optional)</span></label>
                  <input
                    type="date"
                    className="np-input"
                    value={form.meetingDate}
                    onChange={(e) => setForm({ ...form, meetingDate: e.target.value })}
                  />
                </div>
              </div>
              <div className="row g-3 mb-4">
                <div className="col-md-6 np-field">
                  <label>Review Start Date <span className="np-sub fw-normal">(optional)</span></label>
                  <input
                    type="date"
                    className="np-input"
                    value={form.reviewStartDate}
                    onChange={(e) => setForm({ ...form, reviewStartDate: e.target.value })}
                  />
                  <div className="np-hint">Used by AI to tag records as [NEW] or [UPDATED]. Defaults to one year ago.</div>
                </div>
              </div>
              <div className="np-banner np-tone-info mb-0">
                <i className="ri-camera-line"></i>
                <span>A live snapshot of your ISMS (risks, controls, evidence, issues) will be captured automatically when this review is created.</span>
              </div>
            </NimbusModalContent>
          </form>
        </NimbusModal>
      )}
    </div>
  );
}
